// src/encounter/care-plan-tab/use-activity-action.ts
// B1 activity Accept / Sign (care-model's contract, 4 Nov): POST /api/Patients/{patientId}/care-plans/{carePlanId}/activities/
// {activityId}/accept | sign, body { expectedRevision? }. 200 = done or already done (the plan comes back); 403 { reason } =
// not allowed; 409 { reason } = a cancelled item or accept on a signed one; 404 = gone; 412 = changed since read (the
// ACTIVITY's revision, never the plan's). The server stamps who and when. On success the care plans are re-read.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, post } from '@/shared/api/api-client';
import { carePlansKey } from '../services/use-care-plans';

export type ActivityAction = 'accept' | 'sign';

export function activityActionPath(patientId: string, carePlanId: string, activityId: string, action: ActivityAction): string {
  const e = encodeURIComponent;
  return `/api/Patients/${e(patientId)}/care-plans/${e(carePlanId)}/activities/${e(activityId)}/${action}`;
}

/** The refusal in words, from the status the contract defines. */
export function activityActionError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 403 || err.status === 409) {
      // The server's reason, in its words (403 not allowed; 409 a cancelled item, or accept on a signed one).
      try {
        const reason = (JSON.parse(err.body) as { reason?: unknown }).reason;
        if (typeof reason === 'string' && reason.trim()) return reason.trim();
      } catch {
        // not JSON: fall through to the plain words
      }
      return err.status === 403 ? 'You cannot do this for this lane.' : 'This plan item cannot change now.';
    }
    if (err.status === 404) return 'This plan item is no longer on the plan.';
    if (err.status === 412) return 'The plan changed since it was read. It has been read again; try once more.';
  }
  return 'Not saved. Try again.';
}

export function useActivityAction(patientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { carePlanId: string; activityId: string; action: ActivityAction; revision: number | null }) =>
      post<unknown>(
        activityActionPath(patientId, v.carePlanId, v.activityId, v.action),
        v.revision === null ? {} : { expectedRevision: v.revision },
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: carePlansKey(patientId) }),
  });
}
